import { memo, useCallback } from 'react';

import { useTrackAgentPillClicked } from '@air/analytics';
import { BoardIcon } from '@air/design-icons';

import { WorkspaceAgentPillThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPillThumbnail';
import { WorkspaceAgentPreviewCardCloseButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPreviewCardCloseButton';
import { WorkspaceAgentReferencePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentReferencePill';
import { WorkspaceAgentBoardPreviewCard } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentBoardPreviewCard';
import { useOpenWorkspaceAgentBoardPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentBoardPill';
import { useWorkspaceAgentPreviewCardDisclosure } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPreviewCardDisclosure';
import { usePrivateWorkspaceAgentPillBoard } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentPillBoard';
import { preserveWorkspaceAgentComposerFocus } from '~/components/v2/components/workspace/workspace-agent-panel/utils/preserveWorkspaceAgentComposerFocus';
import { getWorkspaceAgentBoardLocation } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentLocationLabels';

export type PrivateWorkspaceAgentComposerBoardPillProps = {
  boardId: string;
  isSelected: boolean;
  label: string;
  onCardClose: () => void;
  onRemove: () => void;
  thumbnailUrl: string | null;
};

export const PrivateWorkspaceAgentComposerBoardPill = memo(
  ({
    boardId,
    isSelected,
    label,
    onCardClose,
    onRemove,
    thumbnailUrl,
  }: PrivateWorkspaceAgentComposerBoardPillProps) => {
    const { openBoardPill } = useOpenWorkspaceAgentBoardPill();
    const { trackAgentPillClicked } = useTrackAgentPillClicked();
    const handleCardOpen = useCallback(() => {
      trackAgentPillClicked({ object: 'board', object_count: 1, object_name: label });
    }, [label, trackAgentPillClicked]);
    const {
      closeCard,
      handleOpenChange,
      isCardOpen,
      navigateFromCard,
      restoreFocusAfterClose,
      suppressEditorFocusRestore,
    } = useWorkspaceAgentPreviewCardDisclosure({
      focusNavigationOrigin: onCardClose,
      // Always: a board node without an id never reaches the chip, and the board row the card draws
      // is fetched only while it is open.
      hasSubject: true,
      onCardClose,
      onCardOpen: handleCardOpen,
    });

    const { data: board, isLoading } = usePrivateWorkspaceAgentPillBoard({ boardId }, { enabled: isCardOpen });

    /** `navigateFromCard` restores the editor first: the popover suppresses Radix's close autofocus, so otherwise focus would land on `<body>`. */
    const handleOpenBoard = useCallback(() => {
      if (board) {
        navigateFromCard(() => openBoardPill(board));
      }
    }, [board, openBoardPill, navigateFromCard]);

    return (
      <WorkspaceAgentReferencePill
        card={
          <WorkspaceAgentBoardPreviewCard
            closeButton={
              <WorkspaceAgentPreviewCardCloseButton
                onClose={closeCard}
                onMouseDown={preserveWorkspaceAgentComposerFocus}
              />
            }
            // `||`, not `??`: an empty-string cover would render `<img src="">` and suppress the fallback.
            coverImageUrl={board?.coverImage?.url || board?.thumbnails?.[0] || null}
            isLoading={isLoading}
            itemCount={board?.directDescendantCount ?? null}
            location={board ? getWorkspaceAgentBoardLocation(board) : null}
            onBodyMouseDown={preserveWorkspaceAgentComposerFocus}
            onOpen={handleOpenBoard}
            referenceId={boardId}
            title={board?.title ?? null}
          />
        }
        isOpen={isCardOpen}
        isSelected={isSelected}
        kind="Board"
        leadingVisual={
          <WorkspaceAgentPillThumbnail
            fallback={<BoardIcon data-testid="WORKSPACE_AGENT_PILL_BOARD_ICON" size="xs" />}
            testId="WORKSPACE_AGENT_PILL_THUMBNAIL"
            url={thumbnailUrl}
          />
        }
        onOpenChange={handleOpenChange}
        onOutsideDismiss={suppressEditorFocusRestore}
        onRemove={onRemove}
        restoreFocusAfterClose={restoreFocusAfterClose}
        surface="composer"
        title={label}
        triggerTestId="WORKSPACE_AGENT_PILL_TRIGGER"
      />
    );
  },
);

PrivateWorkspaceAgentComposerBoardPill.displayName = 'PrivateWorkspaceAgentComposerBoardPill';
